import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAdventLeaderboard } from "../../features/competition/competitionSlice";

const AdventLeaderboard = () => {
  const dispatch = useDispatch();
  const { adventLeaderboard } = useSelector((state) => state.competitions);
  const [isLoading, setIsLoading] = useState(true);
  const [skullParticles, setSkullParticles] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await dispatch(getAdventLeaderboard("apex-week-of-horror"));
      setIsLoading(false);
    };
    fetchData();
  }, [dispatch]);

  // Spawn floating skulls periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const newSkull = {
        id: Date.now(),
        left: Math.random() * 90 + 5,
        delay: Math.random() * 2,
      };
      setSkullParticles((prev) => [...prev, newSkull]);

      setTimeout(() => {
        setSkullParticles((prev) => prev.filter((s) => s.id !== newSkull.id));
      }, 8000);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <div className="leaderboard-loading">
        <div className="loading-skull">💀</div>
        <div className="loading-text">Summoning the fallen...</div>
      </div>
    );
  }

  const leaderboard = adventLeaderboard?.leaderboard || [];

  // Medal icons for top 3
  const getMedalIcon = (rank) => {
    switch (rank) {
      case 1:
        return "👑";
      case 2:
        return "🥈";
      case 3:
        return "🥉";
      default:
        return null;
    }
  };

  const getRankClass = (rank) => {
    if (rank === 1) return "rank-first";
    if (rank === 2) return "rank-second";
    if (rank === 3) return "rank-third";
    return "";
  };

  return (
    <>
      <style jsx="true">{`
        @import url("https://fonts.googleapis.com/css2?family=Creepster&family=Nosifer&family=Griffy&family=Fira+Code:wght@400;600&display=swap");

        .leaderboard-container {
          font-family: "Griffy", serif;
          background: linear-gradient(
            145deg,
            rgba(45, 27, 105, 0.85),
            rgba(10, 10, 10, 0.95)
          );
          border: 3px solid #b7410e;
          border-radius: 20px;
          padding: 3rem 2.5rem;
          position: relative;
          overflow: hidden;
          box-shadow: 0 0 50px rgba(255, 69, 0, 0.35),
            inset 0 0 80px rgba(45, 27, 105, 0.3);
          animation: containerPulse 0.8s ease-out;
        }

        @keyframes containerPulse {
          0% {
            transform: scale(0.95);
            opacity: 0;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .leaderboard-container::before {
          content: "";
          position: absolute;
          top: -3px;
          left: -3px;
          right: -3px;
          bottom: -3px;
          background: linear-gradient(
            45deg,
            #ff4500,
            #39ff14,
            #ff4500,
            #39ff14
          );
          border-radius: 20px;
          z-index: -1;
          opacity: 0.25;
          filter: blur(12px);
          animation: borderRotate 6s linear infinite;
        }

        @keyframes borderRotate {
          0% {
            filter: blur(12px) hue-rotate(0deg);
          }
          100% {
            filter: blur(12px) hue-rotate(360deg);
          }
        }

        /* Floating skull particles */
        .skull-particle {
          position: absolute;
          font-size: 1.8rem;
          opacity: 0;
          pointer-events: none;
          animation: floatSkull 8s ease-in-out forwards;
          z-index: 1;
          filter: drop-shadow(0 0 8px rgba(255, 69, 0, 0.6));
        }

        @keyframes floatSkull {
          0% {
            transform: translateY(100%) rotate(0deg) scale(0.8);
            opacity: 0;
          }
          10% {
            opacity: 0.5;
          }
          90% {
            opacity: 0.4;
          }
          100% {
            transform: translateY(-900px) rotate(720deg) scale(1.2);
            opacity: 0;
          }
        }

        /* Loading state */
        .leaderboard-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 500px;
          background: linear-gradient(
            145deg,
            rgba(45, 27, 105, 0.9),
            rgba(10, 10, 10, 0.95)
          );
          border: 3px solid #b7410e;
          border-radius: 20px;
          gap: 1.5rem;
        }

        .loading-skull {
          font-size: 5rem;
          animation: skullBounce 1.2s ease-in-out infinite;
          filter: drop-shadow(0 0 20px rgba(255, 69, 0, 0.8));
        }

        @keyframes skullBounce {
          0%,
          100% {
            transform: translateY(0) rotate(0deg) scale(1);
          }
          25% {
            transform: translateY(-15px) rotate(-10deg) scale(1.05);
          }
          75% {
            transform: translateY(-15px) rotate(10deg) scale(1.05);
          }
        }

        .loading-text {
          font-family: "Creepster", cursive;
          font-size: 1.8rem;
          color: #39ff14;
          text-shadow: 0 0 15px #39ff14, 2px 2px 0 #0a0a0a;
          letter-spacing: 2px;
          animation: textFlicker 2s infinite;
        }

        @keyframes textFlicker {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }

        /* Table styling */
        .leaderboard-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0 1rem;
          position: relative;
          z-index: 10;
        }

        .leaderboard-header-row {
          font-family: "Creepster", cursive;
          font-size: 1.1rem;
          text-transform: uppercase;
          letter-spacing: 3px;
          color: #c0c5ce;
        }

        .leaderboard-header-row th {
          padding: 1.2rem 1.5rem;
          text-align: left;
          border-bottom: 3px solid #b7410e;
          text-shadow: 0 0 10px rgba(183, 65, 14, 0.6), 1px 1px 0 #0a0a0a;
          position: relative;
        }

        .leaderboard-header-row th::after {
          content: "";
          position: absolute;
          bottom: -3px;
          left: 0;
          width: 100%;
          height: 3px;
          background: linear-gradient(90deg, transparent, #ff4500, transparent);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .leaderboard-header-row th:hover::after {
          opacity: 0.6;
        }

        .leaderboard-header-row th:last-child,
        .leaderboard-header-row th:nth-child(3) {
          text-align: right;
        }

        .leaderboard-row {
          background: linear-gradient(
            135deg,
            rgba(183, 65, 14, 0.18),
            rgba(10, 10, 10, 0.75)
          );
          border: 2px solid rgba(183, 65, 14, 0.4);
          transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          animation: rowFadeIn 0.6s ease-out backwards;
          position: relative;
        }

        @keyframes rowFadeIn {
          from {
            opacity: 0;
            transform: translateX(-40px) rotateY(-15deg);
          }
          to {
            opacity: 1;
            transform: translateX(0) rotateY(0);
          }
        }

        .leaderboard-row::before {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: linear-gradient(180deg, #ff4500, #39ff14);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .leaderboard-row:hover::before {
          opacity: 1;
        }

        .leaderboard-row:hover {
          background: linear-gradient(
            135deg,
            rgba(183, 65, 14, 0.35),
            rgba(45, 27, 105, 0.6)
          );
          border-color: #ff4500;
          transform: translateX(8px) scale(1.01);
          box-shadow: 0 6px 30px rgba(255, 69, 0, 0.5),
            inset 0 0 20px rgba(255, 69, 0, 0.1);
        }

        .leaderboard-row td {
          padding: 1.5rem 1.5rem;
          font-size: 1.15rem;
          color: #f8f8e8;
          border-top: 2px solid transparent;
          border-bottom: 2px solid transparent;
        }

        .leaderboard-row td:first-child {
          border-left: 2px solid rgba(183, 65, 14, 0.4);
          border-top-left-radius: 12px;
          border-bottom-left-radius: 12px;
        }

        .leaderboard-row td:last-child {
          border-right: 2px solid rgba(183, 65, 14, 0.4);
          border-top-right-radius: 12px;
          border-bottom-right-radius: 12px;
        }

        /* Rank styling */
        .rank-cell {
          display: flex;
          align-items: center;
          gap: 1rem;
          font-family: "Fira Code", monospace;
          font-weight: 600;
        }

        .rank-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 3rem;
          height: 3rem;
          border-radius: 50%;
          background: linear-gradient(135deg, #2d1b69, #0a0a0a);
          border: 2px solid #b7410e;
          font-size: 1.2rem;
          color: #c0c5ce;
          box-shadow: 0 0 20px rgba(57, 255, 20, 0.2),
            inset 0 2px 5px rgba(0, 0, 0, 0.5);
          transition: all 0.3s ease;
        }

        .leaderboard-row:hover .rank-badge {
          transform: rotate(360deg) scale(1.1);
        }

        /* Top 3 special styling */
        .rank-first .rank-badge {
          background: linear-gradient(135deg, #ffd700, #ff8c00);
          border-color: #ffd700;
          color: #0a0a0a;
          font-weight: 900;
          font-size: 1.3rem;
          box-shadow: 0 0 35px rgba(255, 215, 0, 0.7),
            inset 0 2px 8px rgba(255, 255, 255, 0.4);
          animation: goldGlow 2.5s ease-in-out infinite;
        }

        @keyframes goldGlow {
          0%,
          100% {
            box-shadow: 0 0 35px rgba(255, 215, 0, 0.7),
              inset 0 2px 8px rgba(255, 255, 255, 0.4);
          }
          50% {
            box-shadow: 0 0 55px rgba(255, 215, 0, 1),
              inset 0 2px 12px rgba(255, 255, 255, 0.6);
          }
        }

        .rank-second .rank-badge {
          background: linear-gradient(135deg, #e8e8e8, #999999);
          border-color: #c0c0c0;
          color: #0a0a0a;
          font-weight: 900;
          box-shadow: 0 0 30px rgba(192, 192, 192, 0.6),
            inset 0 2px 6px rgba(255, 255, 255, 0.3);
        }

        .rank-third .rank-badge {
          background: linear-gradient(135deg, #d4964f, #8b4513);
          border-color: #cd7f32;
          color: #0a0a0a;
          font-weight: 900;
          box-shadow: 0 0 25px rgba(205, 127, 50, 0.6),
            inset 0 2px 6px rgba(255, 255, 255, 0.2);
        }

        .medal-icon {
          font-size: 1.8rem;
          animation: medalFloat 2.5s ease-in-out infinite;
          filter: drop-shadow(0 0 8px rgba(255, 215, 0, 0.6));
        }

        @keyframes medalFloat {
          0%,
          100% {
            transform: translateY(0) rotate(-8deg) scale(1);
          }
          50% {
            transform: translateY(-8px) rotate(8deg) scale(1.1);
          }
        }

        /* Name styling */
        .name-cell {
          font-family: "Creepster", cursive;
          font-size: 1.4rem;
          color: #39ff14;
          text-shadow: 0 0 12px #39ff14, 2px 2px 0 #0a0a0a;
          letter-spacing: 1.5px;
          transition: all 0.3s ease;
        }

        .leaderboard-row:hover .name-cell {
          color: #ff4500;
          text-shadow: 0 0 18px #ff4500, 2px 2px 0 #0a0a0a;
        }

        .rank-first .name-cell {
          color: #ffd700;
          font-size: 1.6rem;
          text-shadow: 0 0 20px #ffd700, 0 0 40px #ff8c00, 2px 2px 0 #0a0a0a;
        }

        .rank-first:hover .name-cell {
          animation: nameShine 1.5s ease-in-out infinite;
        }

        @keyframes nameShine {
          0%,
          100% {
            text-shadow: 0 0 20px #ffd700, 0 0 40px #ff8c00, 2px 2px 0 #0a0a0a;
          }
          50% {
            text-shadow: 0 0 30px #ffd700, 0 0 60px #ff8c00, 2px 2px 0 #0a0a0a;
          }
        }

        /* Points styling */
        .points-cell {
          text-align: right;
          font-family: "Fira Code", monospace;
          font-weight: 700;
          font-size: 1.3rem;
          color: #ff4500;
          text-shadow: 0 0 12px rgba(255, 69, 0, 0.6);
          transition: all 0.3s ease;
        }

        .leaderboard-row:hover .points-cell {
          color: #39ff14;
          text-shadow: 0 0 15px rgba(57, 255, 20, 0.8);
        }

        .rank-first .points-cell {
          font-size: 1.5rem;
          animation: pointsGlow 2s ease-in-out infinite;
        }

        @keyframes pointsGlow {
          0%,
          100% {
            text-shadow: 0 0 15px rgba(255, 69, 0, 0.6);
          }
          50% {
            text-shadow: 0 0 25px rgba(255, 69, 0, 1);
          }
        }

        /* Challenges styling */
        .challenges-cell {
          text-align: right;
          font-family: "Fira Code", monospace;
          color: #c0c5ce;
          font-size: 1.1rem;
          font-weight: 500;
        }

        /* Empty state */
        .empty-state {
          text-align: center;
          padding: 5rem 2rem;
          position: relative;
          z-index: 10;
        }

        .empty-icon {
          font-size: 6rem;
          margin-bottom: 1.5rem;
          animation: emptyFloat 3.5s ease-in-out infinite;
          filter: drop-shadow(0 0 20px rgba(192, 197, 206, 0.4));
        }

        @keyframes emptyFloat {
          0%,
          100% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }

        .empty-text {
          font-family: "Creepster", cursive;
          font-size: 2.2rem;
          color: #39ff14;
          text-shadow: 0 0 15px #39ff14, 2px 2px 0 #0a0a0a;
          letter-spacing: 3px;
          margin-bottom: 1rem;
        }

        .empty-subtext {
          font-size: 1.2rem;
          color: #c0c5ce;
          font-style: italic;
          line-height: 1.6;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .leaderboard-container {
            padding: 2rem 1.5rem;
          }

          .leaderboard-table {
            border-spacing: 0 0.7rem;
          }

          .leaderboard-row td {
            padding: 1.2rem 1rem;
            font-size: 1rem;
          }

          .name-cell {
            font-size: 1.2rem;
          }

          .rank-first .name-cell {
            font-size: 1.3rem;
          }

          .rank-badge {
            min-width: 2.5rem;
            height: 2.5rem;
            font-size: 1rem;
          }

          .medal-icon {
            font-size: 1.5rem;
          }

          .points-cell {
            font-size: 1.1rem;
          }

          .challenges-cell,
          .leaderboard-header-row th:last-child {
            display: none;
          }

          .empty-icon {
            font-size: 4rem;
          }

          .empty-text {
            font-size: 1.6rem;
          }

          .empty-subtext {
            font-size: 1rem;
          }
        }
      `}</style>

      <div className="leaderboard-container">
        {/* Floating skull particles */}
        {/* {skullParticles.map((skull) => (
          <div
            key={skull.id}
            className="skull-particle"
            style={{
              left: `${skull.left}%`,
              bottom: 0,
              animationDelay: `${skull.delay}s`,
            }}
          >
            💀
          </div>
        ))} */}

        {leaderboard.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🕸️</div>
            <div className="empty-text">The Darkness Awaits</div>
            <div className="empty-subtext">
              No souls have dared to enter...
              <br />
              Will you be the first to brave the shadows?
            </div>
          </div>
        ) : (
          <table className="leaderboard-table">
            <thead>
              <tr className="leaderboard-header-row">
                <th>Rank</th>
                <th>Name</th>
                <th>Points</th>
                <th>Challenges</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((entry, index) => (
                <tr
                  key={entry._id}
                  className={`leaderboard-row ${getRankClass(entry.rank)}`}
                  style={{ animationDelay: `${index * 0.08}s` }}
                >
                  <td>
                    <div className="rank-cell">
                      <div className="rank-badge">{entry.rank}</div>
                      {getMedalIcon(entry.rank) && (
                        <span className="medal-icon">
                          {getMedalIcon(entry.rank)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="name-cell">{entry.displayName}</td>
                  <td className="points-cell">
                    {entry.totalPoints.toLocaleString()}
                  </td>
                  <td className="challenges-cell">
                    {entry.challengesCompleted}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export default AdventLeaderboard;
